import { IdentificationIcon } from "@heroicons/react/24/solid";
import AddButton from "components/AddButton";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import TableFilters from "../TableFilters";
import MaterialTableTS from "components/tables/MaterialTableTS";
import React, { useEffect, useState } from "react";
import BusLicensesPermitsTypeForm from "../form/BusLicensesPermitsTypeForm";
import EditButton from "components/EditButton";
import Timer from "components/Timer";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "components/toastNotification";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useGetTypeList from "../hooks/useGetType";
import useDeleteType from "../hooks/useDeleteType";
import {
  addStateBusLicensePermitType,
  busLicensePermitTypeFetching,
  busLicensePermitTypeFilterText,
  editStateBusLicensePermitType,
  rowEditBusLicensePermitType,
  selectedRowsStateBusLicensePermitType,
} from "../atoms/atoms";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { IBusLicensePermitType } from "../types";

export default function BusLicensesPermitsTypeTable() {
  const { data: directData, refetch: reFetch } = useGetTypeList();

  const BulkDelete = useDeleteType();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateBusLicensePermitType
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateBusLicensePermitType
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateBusLicensePermitType
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    busLicensePermitTypeFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditBusLicensePermitType);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(
    busLicensePermitTypeFetching
  );

  //Local States
  const [tableData, setTableData] = useState<IBusLicensePermitType[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    IBusLicensePermitType[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_businesslicensespermitsconfiguration_type_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_businesslicensespermitsconfiguration_type_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_businesslicensespermitsconfiguration_type_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  // Get Data
  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  //For filtered data update
  const [dataId] = useState("");

  useEffect(() => {
    if (dataId !== "") {
      if (dataId?.includes(".")) {
        let ids = dataId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = tableData?.filter((x) =>
          arr.includes(x.busLicensePermitTypeId)
        );
        setTableFilteredData(filteredData);
      } else {
        const filteredData = tableData?.filter(
          (x) => x.busLicensePermitTypeId === Number(dataId)
        );
        setTableFilteredData(filteredData);
      }
    }
  }, [dataId, tableData]);

  let location = useLocation();
  let recordIdFromRecent;
  const history = useHistory();
  const [recent, setRecent] = useState("");

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          if (typeof result !== "object") {
            toast.fire("Failed", result, "error");
          } else {
            setTableFilteredData(result);
            setRecent(result[0].busLicensePermitTypeName);
          }
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent, history]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids =
      selectedRows && selectedRows.map((row) => row.busLicensePermitTypeId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1
        ? "Business License Permit Types"
        : "Business License Permit Type"
    }?`}</div>
  );

  const filteredItems = tableData
    ? tableData?.filter(
        (item) =>
          item.busLicensePermitTypeName &&
          item.busLicensePermitTypeName
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const matColumns = [
    {
      title: "Business License Permit Type Name",
      field: "busLicensePermitTypeName",
      customSort: (a, b) =>
        a.busLicensePermitTypeName && b.busLicensePermitTypeName
          ? a.busLicensePermitTypeName
              .trim()
              .localeCompare(b.busLicensePermitTypeName.trim())
          : a.busLicensePermitTypeName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div className="animate-fade-in-down">
      <div className="mx-8 py-4">
        {!(actionAddState || actionEditState) && (
          <div>
            <div className="flex gap-2 items-center">
              <div>
                <IdentificationIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              </div>
              <div className="flex flex-col leading tight">
                <h3 className="text-xs font-medium text-gray-500">
                  Utilities Module
                </h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  BUSINESS LICENSES AND PERMITS CONFIGURATION
                </h1>
              </div>
            </div>
            <div className="h-0.5 w-full border-b-2 border-secondary mt-2 mb-4" />
          </div>
        )}
        {actionAddState || actionEditState ? (
          <BusLicensesPermitsTypeForm reFetch={reFetch} />
        ) : (
          <>
            <div className="flex justify-between text-xs items-center w-full">
              <TableFilters
                defaultText={filterTextState}
                recentText={recent}
                setRecent={setRecent}
                textInputName={"BusLicensePermitTypeName"}
                columnDetails={"Business License Permit Type Name"}
                filterText={setFilterTextState}
                disabledButton={
                  actionEditState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div>
                {userPermissions.includes(addPermission) && (
                  <AddButton
                    buttonText="New Business License Permit Type"
                    onClick={() => handleAdd()}
                    disabled={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                  />
                )}
              </div>
            </div>

            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div className="">
              <MaterialTableTS
                data={
                  isFetching
                    ? []
                    : tableFilteredData?.length > 0
                    ? tableFilteredData
                    : filterTextState === ""
                    ? tableData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                isLoading={isFetching}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={
                  !actionEditState &&
                  !actionAddState &&
                  userPermissions.includes(deletePermission)
                }
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Business License Permit Type"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </div>
  );
}
